import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import KpiGateModal from '../components/KpiGateModal';
import ProjectIntakeModal from '../components/ProjectIntakeModal';
import MarkdownMessage from '../components/MarkdownMessage';
import { KPI_SECTIONS } from '../constants/kpiSections';
import {
  getKpiProfile,
  updateKpiProfile,
  getChatSessions,
  createChatSession,
  getSessionMessages,
  sendSessionMessage,
  deleteChatSession,
  renameChatSession,
  getMilestones,
  generateMilestones as apiGenerateMilestones,
  updateMilestone as apiUpdateMilestone,
  listProjects as apiListProjects,
  getProject as apiGetProject,
  updateProject as apiUpdateProject,
  deleteProject as apiDeleteProject,
  listProjectSessions as apiListProjectSessions,
  createProjectSession as apiCreateProjectSession,
} from '../api';
import './Profile.css';

/**
 * Profile.jsx — the Chat workspace
 *
 * Routes:
 *   /chat                                 — standalone chat welcome
 *   /chat/:sessionId                      — a standalone chat thread
 *   /projects                             — project list in the center pane
 *   /projects/:projectId                  — project detail: composer + chat rows
 *   /projects/:projectId/chat/:sessionId  — a chat inside a project
 *
 * Layout: left = projects + chat history + settings shortcuts, center =
 * conversation / project views, right = KPI snapshot (user-level, or the
 * selected project's persona-weighted metrics + capital risk read) +
 * milestones. Gated by the KPI onboarding modal (KpiGateModal) on the
 * standalone chat views until the user's 5-KPI profile is filled in.
 *
 * Project metrics and the risk read are ALWAYS server-computed — the
 * intake questionnaire and figure edits never call the LLM; tokens are
 * only spent when a message is actually sent to the assistant.
 *
 * File kept as Profile.jsx/.css to avoid an unnecessary rename.
 */

/* ─── Icons ──────────────────────────────────────────────────────────────── */
function MenuIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function SparkleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.5L9.2 6.8L14.5 8L9.2 9.2L8 14.5L6.8 9.2L1.5 8L6.8 6.8L8 1.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 1.5v11M1.5 7h11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 3.5h9M5.5 3.5V2a1 1 0 011-1h1a1 1 0 011 1v1.5M3.5 3.5l.5 8.5a1 1 0 001 1h4a1 1 0 001-1l.5-8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function EditIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M9.5 2L12 4.5L5 11.5L2 12L2.5 9L9.5 2Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M14 2L7 9M14 2L9.5 14L7 9M14 2L2 6.5L7 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function GearIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M8 1.5v1M8 13.5v1M1.5 8h1M13.5 8h1M3.11 3.11l.71.71M12.18 12.18l.71.71M12.18 3.82l-.71.71M4.54 11.46l-.71.71" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}
function HistoryIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M8 5.5V8l2 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function FolderIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M1.5 4.5a1 1 0 011-1h3l1.5 2h6a1 1 0 011 1v6a1 1 0 01-1 1h-10.5a1 1 0 01-1-1v-8z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    </svg>
  );
}
function ChevronRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const timeAgo = (dateStr) => {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const RISK_LABEL = {
  low: 'Low', moderate: 'Moderate', elevated: 'Elevated', high: 'High', unknown: 'Not assessed',
};

const fmtKpiValue = (descriptor, value) => {
  if (value === null || value === undefined) return '—';
  return `${descriptor.prefix || ''}${typeof value === 'number' ? value.toLocaleString() : value}${descriptor.suffix || ''}`;
};

/* ─── Left Sidebar — Projects + Chat History + Settings ─────────────────── */
const LeftSidebar = ({
  sessions, activeSessionId, onNewChat, onSelectSession, onDeleteSession, mobileOpen, onMobileClose, user,
  renamingSessionId, renameDraft, onStartRename, onRenameDraftChange, onSubmitRename, onCancelRename,
  projects, activeProjectId, onNewProject, onSelectProject, onViewAllProjects,
}) => (
  <>
    {mobileOpen && <div className="chat-drawer-overlay" onClick={onMobileClose} />}
    <aside className={`chat-sidebar${mobileOpen ? ' chat-sidebar--mobile-open' : ''}`}>
      <div className="chat-sidebar__header">
        <span className="chat-sidebar__logo">MarginPro</span>
        <button className="chat-sidebar__close-btn" onClick={onMobileClose} aria-label="Close menu">
          <CloseIcon />
        </button>
      </div>

      <button className="chat-sidebar__new-btn" onClick={onNewChat}>
        <PlusIcon /> New Chat
      </button>

      {/* ─── Projects (above chats) ──────────────────────────────────────── */}
      <div className="chat-sidebar__section-label chat-sidebar__section-label--row">
        <span className="chat-sidebar__section-label-text"><FolderIcon /> Projects</span>
        <button className="chat-sidebar__mini-btn" onClick={onNewProject} aria-label="New project">
          <PlusIcon />
        </button>
      </div>
      <nav className="chat-sidebar__projects">
        {projects.length === 0 ? (
          <p className="chat-sidebar__empty">No projects yet.</p>
        ) : (
          projects.slice(0, 6).map((p) => (
            <button
              key={p._id}
              className={`chat-sidebar__project${p._id === activeProjectId ? ' chat-sidebar__project--active' : ''}`}
              onClick={() => onSelectProject(p._id)}
            >
              <span className="chat-sidebar__project-name">{p.name}</span>
              <span className={`chat-sidebar__project-risk chat-sidebar__project-risk--${p.risk?.level || 'unknown'}`} title={`Capital risk: ${RISK_LABEL[p.risk?.level || 'unknown']}`} />
            </button>
          ))
        )}
      </nav>
      <button className="chat-sidebar__view-all chat-sidebar__view-all--btn" onClick={onViewAllProjects}>
        View all projects →
      </button>

      {/* ─── Chat history (standalone chats) ─────────────────────────────── */}
      <div className="chat-sidebar__section-label">
        <HistoryIcon /> Chat History
      </div>
      <nav className="chat-sidebar__sessions">
        {sessions.length === 0 ? (
          <p className="chat-sidebar__empty">No chats yet — start one below.</p>
        ) : (
          sessions.slice(0, 20).map((s) => {
            const isRenaming = renamingSessionId === s._id;
            return (
              <div
                key={s._id}
                className={`chat-sidebar__session${s._id === activeSessionId ? ' chat-sidebar__session--active' : ''}${isRenaming ? ' chat-sidebar__session--renaming' : ''}`}
                onClick={() => !isRenaming && onSelectSession(s._id)}
              >
                <div className="chat-sidebar__session-text">
                  {isRenaming ? (
                    <input
                      className="chat-sidebar__session-rename-input"
                      value={renameDraft}
                      autoFocus
                      maxLength={150}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => onRenameDraftChange(e.target.value)}
                      onBlur={() => onSubmitRename()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); onSubmitRename(); }
                        if (e.key === 'Escape') { e.preventDefault(); onCancelRename(); }
                      }}
                    />
                  ) : (
                    <>
                      <span className="chat-sidebar__session-title">{s.title}</span>
                      <span className="chat-sidebar__session-time">{timeAgo(s.lastMessageAt)}</span>
                    </>
                  )}
                </div>
                {!isRenaming && (
                  <div className="chat-sidebar__session-actions">
                    <button
                      className="chat-sidebar__session-rename"
                      onClick={(e) => { e.stopPropagation(); onStartRename(s._id, s.title); }}
                      aria-label="Rename chat"
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="chat-sidebar__session-delete"
                      onClick={(e) => { e.stopPropagation(); onDeleteSession(s._id); }}
                      aria-label="Delete chat"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </nav>
      {sessions.length > 20 && (
        <Link to="/chat/history" className="chat-sidebar__view-all">View all chats →</Link>
      )}

      <div className="chat-sidebar__footer">
        <div className="chat-sidebar__section-label">
          <GearIcon /> Settings
        </div>
        <Link to="/settings/info" className="chat-sidebar__settings-link">My Info</Link>
        <Link to="/settings/kpis" className="chat-sidebar__settings-link">KPI's</Link>
        {user?.firstName && (
          <div className="chat-sidebar__user">
            <div className="chat-sidebar__user-avatar">{user.firstName[0]}{user.lastName?.[0] || ''}</div>
            <span className="chat-sidebar__user-name">{user.firstName} {user.lastName}</span>
          </div>
        )}
      </div>
    </aside>
  </>
);

/* ─── Center — Projects list (row by row) ───────────────────────────────── */
const ProjectsCenter = ({ projects, loading, onSelectProject, onNewProject, onDeleteProject, onOpenLeftDrawer, onOpenRightDrawer }) => (
  <main className="chat-center">
    <div className="chat-center__topbar">
      <button className="chat-center__icon-btn" onClick={onOpenLeftDrawer} aria-label="Open menu">
        <MenuIcon />
      </button>
      <span className="chat-center__topbar-title">Projects</span>
      <button className="chat-center__icon-btn" onClick={onOpenRightDrawer} aria-label="Open KPIs & milestones">
        <SparkleIcon />
      </button>
    </div>

    <div className="chat-center__thread project-list">
      {loading ? (
        <p className="chat-center__note">Loading projects…</p>
      ) : projects.length === 0 ? (
        <div className="chat-center__welcome">
          <span className="chat-center__welcome-icon"><FolderIcon /></span>
          <h2 className="chat-center__welcome-title">No projects yet.</h2>
          <p className="chat-center__welcome-sub">
            Projects group related chats and track their own metrics — a quick questionnaire
            tailors which numbers each project follows, computed instantly on our side.
          </p>
          <button className="btn btn--gold" onClick={onNewProject}>
            <PlusIcon /> New Project
          </button>
        </div>
      ) : (
        <>
          <div className="project-list__head">
            <button className="btn btn--sm btn--gold" onClick={onNewProject}>
              <PlusIcon /> New Project
            </button>
          </div>
          {projects.map((p) => (
            <div key={p._id} className="project-row" onClick={() => onSelectProject(p._id)}>
              <div className="project-row__main">
                <span className="project-row__name">{p.name}</span>
                {p.description && <span className="project-row__desc">{p.description}</span>}
                <span className="project-row__meta">
                  {p.chatCount || 0} chat{(p.chatCount || 0) === 1 ? '' : 's'} · {timeAgo(p.lastActivityAt)}
                  {p.status !== 'active' && ` · ${p.status}`}
                </span>
              </div>
              <div className="project-row__side">
                <span className={`project-risk-badge project-risk-badge--${p.risk?.level || 'unknown'}`}>
                  {RISK_LABEL[p.risk?.level || 'unknown']} risk
                </span>
                <button
                  className="chat-sidebar__session-delete"
                  onClick={(e) => { e.stopPropagation(); onDeleteProject(p._id); }}
                  aria-label="Delete project"
                >
                  <TrashIcon />
                </button>
                <ChevronRightIcon />
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  </main>
);

/* ─── Center — Project detail (prompt above, chats listed below) ────────── */
const ProjectDetailCenter = ({
  project, projectSessions, loading, sending, chatError,
  onSend, onSelectSession, onBack, onOpenLeftDrawer, onOpenRightDrawer,
}) => {
  const [input, setInput] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    onSend(text);
  };

  return (
    <main className="chat-center">
      <div className="chat-center__topbar">
        <button className="chat-center__icon-btn" onClick={onOpenLeftDrawer} aria-label="Open menu">
          <MenuIcon />
        </button>
        <button className="chat-center__topbar-title chat-center__topbar-title--editable" onClick={onBack}>
          <FolderIcon /> {project?.name || 'Project'}
        </button>
        <button className="chat-center__icon-btn" onClick={onOpenRightDrawer} aria-label="Open project metrics">
          <SparkleIcon />
        </button>
      </div>

      <div className="chat-center__thread project-detail">
        {loading ? (
          <p className="chat-center__note">Loading project…</p>
        ) : (
          <>
            <div className="project-detail__intro">
              <h2 className="project-detail__title">{project?.name}</h2>
              {project?.description && <p className="project-detail__desc">{project.description}</p>}
              <p className="project-detail__hint">
                Start a new chat below, or continue one — every chat here shares this project's
                metrics and context.
              </p>
            </div>

            {/* Prompt window above the chat list — sends into a NEW project chat */}
            <form className="chat-composer project-detail__composer" onSubmit={handleSubmit}>
              <textarea
                className="chat-composer__input"
                placeholder={`Start a new chat in ${project?.name || 'this project'}…`}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                rows={1}
                maxLength={2000}
                disabled={sending}
              />
              <button type="submit" className="chat-composer__send" disabled={sending || !input.trim()} aria-label="Send">
                <SendIcon />
              </button>
            </form>
            {sending && <p className="chat-center__note">Starting chat…</p>}
            {chatError && <p className="chat-center__error">{chatError}</p>}

            <div className="project-detail__chats-label">Chats in this project</div>
            {projectSessions.length === 0 ? (
              <p className="chat-center__note">No chats yet — send a message above to start one.</p>
            ) : (
              projectSessions.map((s) => (
                <div key={s._id} className="project-row project-row--chat" onClick={() => onSelectSession(s._id)}>
                  <div className="project-row__main">
                    <span className="project-row__name">{s.title}</span>
                    {s.lastMessagePreview && <span className="project-row__desc">{s.lastMessagePreview}</span>}
                    <span className="project-row__meta">{timeAgo(s.lastMessageAt)} · {s.messageCount} message{s.messageCount === 1 ? '' : 's'}</span>
                  </div>
                  <div className="project-row__side">
                    <ChevronRightIcon />
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </main>
  );
};

/* ─── Center — Chat Thread + Composer ────────────────────────────────────── */
const ChatCenter = ({ messages, loading, sending, chatError, onSend, onOpenLeftDrawer, onOpenRightDrawer, session, onRenameSession, breadcrumb, onBreadcrumb }) => {
  const [input, setInput] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const threadRef = useRef(null);

  const startEditingTitle = () => {
    setTitleDraft(session?.title || '');
    setEditingTitle(true);
  };

  const submitTitle = () => {
    setEditingTitle(false);
    const trimmed = titleDraft.trim();
    if (session && trimmed && trimmed !== session.title) {
      onRenameSession(session._id, trimmed);
    }
  };

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, sending]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    onSend(text);
  };

  return (
    <main className="chat-center">
      <div className="chat-center__topbar">
        <button className="chat-center__icon-btn" onClick={onOpenLeftDrawer} aria-label="Open menu">
          <MenuIcon />
        </button>
        {breadcrumb && (
          <button className="chat-center__breadcrumb" onClick={onBreadcrumb}>
            <FolderIcon /> {breadcrumb} <ChevronRightIcon />
          </button>
        )}
        {editingTitle ? (
          <input
            className="chat-center__topbar-title-input"
            value={titleDraft}
            autoFocus
            maxLength={150}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={submitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submitTitle(); }
              if (e.key === 'Escape') { e.preventDefault(); setEditingTitle(false); }
            }}
          />
        ) : session ? (
          <button className="chat-center__topbar-title chat-center__topbar-title--editable" onClick={startEditingTitle}>
            {session.title}
            <EditIcon />
          </button>
        ) : (
          <span className="chat-center__topbar-title">MarginPro Assistant</span>
        )}
        <button className="chat-center__icon-btn" onClick={onOpenRightDrawer} aria-label="Open KPIs & milestones">
          <SparkleIcon />
        </button>
      </div>

      <div className="chat-center__thread" ref={threadRef}>
        {loading ? (
          <p className="chat-center__note">Loading conversation…</p>
        ) : messages.length === 0 ? (
          <div className="chat-center__welcome">
            <span className="chat-center__welcome-icon"><SparkleIcon /></span>
            <h2 className="chat-center__welcome-title">What are you working through today?</h2>
            <p className="chat-center__welcome-sub">
              Ask about your margins, break-even math, cash runway, or what to focus on next —
              grounded in your KPI profile. Not legal, tax, or investment advice.
            </p>
          </div>
        ) : (
          messages.map((m) => (
            <div
              key={m._id || `${m.role}-${m.content.slice(0, 12)}-${Math.random()}`}
              className={`chat-turn chat-turn--${m.role}`}
            >
              {m.role === 'assistant' ? (
                <>
                  <div className="chat-turn__label">
                    <SparkleIcon /> MarginPro Assistant
                    {m.source === 'server' && <span className="chat-turn__source-tag">Computed</span>}
                  </div>
                  <MarkdownMessage content={m.content} />
                </>
              ) : (
                <>
                  <div className="chat-turn__label chat-turn__label--user">You</div>
                  <p className="chat-turn__user-text">{m.content}</p>
                </>
              )}
            </div>
          ))
        )}
        {sending && (
          <div className="chat-turn chat-turn--assistant">
            <div className="chat-turn__label">
              <SparkleIcon /> MarginPro Assistant
            </div>
            <div className="chat-typing">
              <span className="chat-typing-dot" />
              <span className="chat-typing-dot" />
              <span className="chat-typing-dot" />
            </div>
          </div>
        )}
      </div>

      {chatError && <p className="chat-center__error">{chatError}</p>}

      <form className="chat-composer" onSubmit={handleSubmit}>
        <textarea
          className="chat-composer__input"
          placeholder="Message the MarginPro Assistant…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          rows={1}
          maxLength={2000}
          disabled={sending}
        />
        <button type="submit" className="chat-composer__send" disabled={sending || !input.trim()} aria-label="Send">
          <SendIcon />
        </button>
      </form>
    </main>
  );
};

/* ─── Right Panel — Project figures editor ───────────────────────────────── */
/**
 * Compact editor for the numbers driving a project's computed KPIs +
 * capital risk. Fields are derived from the project's kpiDescriptors
 * (the persona-weighted KPI set the server selected), deduped since
 * several KPIs share inputs. Saving PATCHes the project and everything
 * recomputes server-side — no LLM involved.
 */
const ProjectFiguresEditor = ({ project, onSave, saving }) => {
  const [values, setValues] = useState({});
  const [capital, setCapital] = useState({ available: '', committed: '', monthlyBurn: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    const initial = {};
    (project.kpiDescriptors || []).forEach((d) => {
      (d.inputs || []).forEach((inp) => {
        if (initial[inp.name] === undefined) {
          initial[inp.name] = project.kpiInputs?.[inp.name] ?? '';
        }
      });
    });
    setValues(initial);
    setCapital({
      available: project.capital?.available ?? '',
      committed: project.capital?.committed ?? '',
      monthlyBurn: project.capital?.monthlyBurn ?? '',
    });
  }, [project]);

  // Dedupe input fields across KPIs (many share e.g. earnedValue).
  const fields = [];
  const seen = new Set();
  (project.kpiDescriptors || []).forEach((d) => {
    (d.inputs || []).forEach((inp) => {
      if (seen.has(inp.name)) return;
      seen.add(inp.name);
      fields.push(inp);
    });
  });

  const handleSave = async () => {
    setError('');
    const kpiInputs = {};
    for (const f of fields) {
      const raw = values[f.name];
      if (raw === '' || raw === null || raw === undefined) continue;
      const num = Number(raw);
      if (Number.isNaN(num)) { setError('Numbers only, please.'); return; }
      kpiInputs[f.name] = num;
    }
    const capitalOut = {};
    for (const [k, raw] of Object.entries(capital)) {
      if (raw === '' || raw === null) continue;
      const num = Number(raw);
      if (Number.isNaN(num) || num < 0) { setError('Capital figures must be non-negative numbers.'); return; }
      capitalOut[k] = num;
    }
    try {
      await onSave({ kpiInputs, capital: capitalOut });
    } catch (err) {
      setError(err.message || 'Could not save figures.');
    }
  };

  return (
    <div className="project-figures">
      <div className="project-figures__group-label">Capital</div>
      {[
        { key: 'available', label: 'Capital available' },
        { key: 'committed', label: 'Capital committed' },
        { key: 'monthlyBurn', label: 'Monthly burn' },
      ].map(({ key, label }) => (
        <label key={key} className="project-figures__field">
          <span>{label}</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            value={capital[key]}
            onChange={(e) => setCapital((c) => ({ ...c, [key]: e.target.value }))}
            placeholder="$"
          />
        </label>
      ))}

      {fields.length > 0 && <div className="project-figures__group-label">Metric inputs</div>}
      {fields.map((f) => (
        <label key={f.name} className="project-figures__field">
          <span>{f.label}</span>
          <input
            type="number"
            inputMode="decimal"
            value={values[f.name] ?? ''}
            onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
            placeholder={f.prefix || '#'}
          />
        </label>
      ))}

      {error && <p className="right-panel__inline-error">{error}</p>}
      <button className="btn btn--sm btn--gold project-figures__save" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save & recompute'}
      </button>
    </div>
  );
};

/* ─── Right Panel — KPIs (user or project) + Risk + Milestones ───────────── */
const RightPanel = ({
  profile, milestones, onGenerate, generating, milestonesError, onUpdateStatus, mobileOpen, onMobileClose,
  project, onSaveProjectFigures, projectSaving,
}) => {
  const [showFigures, setShowFigures] = useState(false);
  const computed = profile?.computed || {};
  const canGenerate = !!profile && profile.completionPct > 0;

  return (
    <>
      {mobileOpen && <div className="chat-drawer-overlay" onClick={onMobileClose} />}
      <aside className={`chat-right-panel${mobileOpen ? ' chat-right-panel--mobile-open' : ''}`}>
        <div className="chat-right-panel__header">
          <span className="chat-right-panel__title">{project ? 'Project Metrics' : 'Your Numbers'}</span>
          <button className="chat-sidebar__close-btn" onClick={onMobileClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className="chat-right-panel__inner">
          {project ? (
            <>
              {/* ── Capital risk read (server-computed) ── */}
              <div className={`project-risk-card project-risk-card--${project.risk?.level || 'unknown'}`}>
                <div className="project-risk-card__head">
                  <span className="project-risk-card__label">Capital Risk</span>
                  <span className={`project-risk-badge project-risk-badge--${project.risk?.level || 'unknown'}`}>
                    {RISK_LABEL[project.risk?.level || 'unknown']}
                  </span>
                </div>
                {project.risk?.runwayMonths !== null && project.risk?.runwayMonths !== undefined && (
                  <div className="project-risk-card__stat">
                    <span>Runway (uncommitted)</span><strong>{project.risk.runwayMonths} mo</strong>
                  </div>
                )}
                {project.risk?.commitmentPct !== null && project.risk?.commitmentPct !== undefined && (
                  <div className="project-risk-card__stat">
                    <span>Capital committed</span><strong>{project.risk.commitmentPct}%</strong>
                  </div>
                )}
                {(project.risk?.notes || []).map((n, i) => (
                  <p key={i} className="project-risk-card__note">{n}</p>
                ))}
                <p className="project-risk-card__disclaimer">Educational estimate — not investment advice.</p>
              </div>

              {/* ── Persona-weighted project KPIs (server-computed) ── */}
              <div className="chat-right-panel__kpi-grid">
                {(project.kpiDescriptors || []).map((d) => (
                  <div key={d.key} className="chat-kpi-card">
                    <span className="chat-kpi-card__label">{d.label}</span>
                    <span className="chat-kpi-card__value">
                      {fmtKpiValue(d, project.computedKpis?.[d.key])}
                    </span>
                  </div>
                ))}
              </div>

              <button
                className="chat-right-panel__complete-link chat-right-panel__complete-link--btn"
                onClick={() => setShowFigures((v) => !v)}
              >
                {showFigures ? 'Hide figures ↑' : 'Edit project figures →'}
              </button>
              {showFigures && (
                <ProjectFiguresEditor project={project} onSave={onSaveProjectFigures} saving={projectSaving} />
              )}
            </>
          ) : (
            <>
              <div className="chat-right-panel__kpi-grid">
                {KPI_SECTIONS.map((s) => {
                  const value = computed[s.resultKey];
                  const hasValue = value !== null && value !== undefined;
                  return (
                    <div key={s.key} className="chat-kpi-card">
                      <span className="chat-kpi-card__label">{s.label}</span>
                      <span className="chat-kpi-card__value">
                        {hasValue ? `${value}${s.resultSuffix}` : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
              {(!profile || profile.completionPct < 100) && (
                <Link to="/settings/kpis" className="chat-right-panel__complete-link">
                  {profile?.completionPct ? `${profile.completionPct}% complete — finish your profile →` : 'Set up your KPI profile →'}
                </Link>
              )}
            </>
          )}

          <div className="chat-right-panel__divider" />

          <div className="chat-right-panel__milestones-head">
            <span className="chat-right-panel__title">Milestones</span>
            <button className="btn btn--sm btn--gold" onClick={onGenerate} disabled={generating || !canGenerate}>
              {generating ? 'Generating…' : 'Generate'}
            </button>
          </div>
          {milestonesError && <p className="right-panel__inline-error">{milestonesError}</p>}
          {milestones.length === 0 ? (
            <p className="right-panel__inline-note">No milestones yet.</p>
          ) : (
            <div className="right-panel__milestones-list">
              {milestones.map((m) => (
                <div key={m._id} className="right-panel__milestone-card">
                  <div className="right-panel__milestone-head">
                    <span className={`right-panel__milestone-priority right-panel__milestone-priority--${m.priority}`}>
                      {m.priority}
                    </span>
                    {m.source === 'llm' && <span className="right-panel__milestone-source">AI-suggested</span>}
                  </div>
                  <p className="right-panel__milestone-title">{m.title}</p>
                  {m.description && <p className="right-panel__milestone-desc">{m.description}</p>}
                  <div className="right-panel__milestone-actions">
                    {m.status !== 'completed' && (
                      <button
                        className="btn btn--sm btn--outline"
                        onClick={() => onUpdateStatus(m._id, m.status === 'in_progress' ? 'completed' : 'in_progress')}
                      >
                        {m.status === 'in_progress' ? 'Mark complete' : 'Start'}
                      </button>
                    )}
                    {m.status !== 'dismissed' && m.status !== 'completed' && (
                      <button className="btn btn--sm btn--ghost" onClick={() => onUpdateStatus(m._id, 'dismissed')}>
                        Dismiss
                      </button>
                    )}
                    {(m.status === 'completed' || m.status === 'dismissed') && (
                      <span className="right-panel__milestone-status-label">{m.status.replace('_', ' ')}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

/* ─── Page Root ──────────────────────────────────────────────────────────── */
const Profile = () => {
  const { sessionId, projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Which center view is active is driven entirely by the URL:
  //   /projects                    → project list
  //   /projects/:projectId         → project detail
  //   /projects/:pid/chat/:sid     → chat (within project)
  //   /chat[/:sessionId]           → chat (standalone)
  const inProjectArea = location.pathname.startsWith('/projects');
  const showProjectList = inProjectArea && !projectId;
  const showProjectDetail = inProjectArea && !!projectId && !sessionId;

  const [kpiProfile, setKpiProfile] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [gateSkipped, setGateSkipped] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState('');

  const [milestones, setMilestones] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [milestonesError, setMilestonesError] = useState('');

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [activeProject, setActiveProject] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSessions, setProjectSessions] = useState([]);
  const [projectSaving, setProjectSaving] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);

  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);

  const [renamingSessionId, setRenamingSessionId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');

  // Initial load: KPI profile, standalone session list, milestones, projects.
  useEffect(() => {
    (async () => {
      try {
        const res = await getKpiProfile();
        setKpiProfile(res.data.profile);
      } catch {
        // handled by empty state in RightPanel
      } finally {
        setKpiLoading(false);
      }
    })();

    (async () => {
      try {
        const res = await getChatSessions();
        setSessions(res.data.sessions || []);
      } catch {
        // non-fatal
      }
    })();

    (async () => {
      try {
        const res = await getMilestones();
        setMilestones(res.data.milestones || []);
      } catch {
        // non-fatal
      }
    })();

    (async () => {
      try {
        const res = await apiListProjects();
        setProjects(res.data.projects || []);
      } catch {
        // non-fatal
      } finally {
        setProjectsLoading(false);
      }
    })();
  }, []);

  // Load the active project (+ its chat list) whenever :projectId changes.
  useEffect(() => {
    if (!projectId) {
      setActiveProject(null);
      setProjectSessions([]);
      return;
    }
    setProjectLoading(true);
    (async () => {
      try {
        const [projRes, sessRes] = await Promise.all([
          apiGetProject(projectId),
          apiListProjectSessions(projectId),
        ]);
        setActiveProject(projRes.data.project);
        setProjectSessions(sessRes.data.sessions || []);
      } catch {
        navigate('/projects', { replace: true });
      } finally {
        setProjectLoading(false);
      }
    })();
  }, [projectId, navigate]);

  // Load the active thread whenever the URL's :sessionId changes.
  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    setMessagesLoading(true);
    (async () => {
      try {
        const res = await getSessionMessages(sessionId);
        setMessages(res.data.messages || []);
      } catch {
        setMessages([]);
      } finally {
        setMessagesLoading(false);
      }
    })();
  }, [sessionId]);

  const handleSaveKpiSection = useCallback(async (sectionKey, payload) => {
    const res = await updateKpiProfile({ [sectionKey]: payload });
    setKpiProfile(res.data.profile);
    return res.data.profile;
  }, []);

  // ─── Standalone chat handlers ──────────────────────────────────────────────
  const handleNewChat = useCallback(() => {
    setChatError('');
    navigate('/chat');
    setLeftOpen(false);
  }, [navigate]);

  const handleSelectSession = useCallback((id) => {
    navigate(`/chat/${id}`);
    setLeftOpen(false);
  }, [navigate]);

  const handleDeleteSession = useCallback(async (id) => {
    const prev = sessions;
    setSessions((s) => s.filter((sess) => sess._id !== id));
    try {
      await deleteChatSession(id);
      if (sessionId === id) navigate('/chat');
    } catch {
      setSessions(prev);
    }
  }, [sessions, sessionId, navigate]);

  const handleRenameSession = useCallback(async (id, title) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const prev = sessions;
    setSessions((s) => s.map((sess) => (sess._id === id ? { ...sess, title: trimmed } : sess)));
    setProjectSessions((s) => s.map((sess) => (sess._id === id ? { ...sess, title: trimmed } : sess)));
    try {
      await renameChatSession(id, trimmed);
    } catch {
      setSessions(prev);
    }
  }, [sessions]);

  const handleStartRename = useCallback((id, currentTitle) => {
    setRenamingSessionId(id);
    setRenameDraft(currentTitle);
  }, []);

  const handleSubmitRename = useCallback(() => {
    if (renamingSessionId) handleRenameSession(renamingSessionId, renameDraft);
    setRenamingSessionId(null);
  }, [renamingSessionId, renameDraft, handleRenameSession]);

  const handleCancelRename = useCallback(() => setRenamingSessionId(null), []);

  // ─── Project handlers ─────────────────────────────────────────────────────
  const handleNewProject = useCallback(() => {
    setIntakeOpen(true);
    setLeftOpen(false);
  }, []);

  const handleProjectCreated = useCallback((project, session) => {
    setIntakeOpen(false);
    setProjects((prev) => [project, ...prev.filter((p) => p._id !== project._id)]);
    // Land directly in the server-generated initial results chat.
    if (session) {
      navigate(`/projects/${project._id}/chat/${session._id}`);
    } else {
      navigate(`/projects/${project._id}`);
    }
  }, [navigate]);

  const handleSelectProject = useCallback((id) => {
    navigate(`/projects/${id}`);
    setLeftOpen(false);
  }, [navigate]);

  const handleViewAllProjects = useCallback(() => {
    navigate('/projects');
    setLeftOpen(false);
  }, [navigate]);

  const handleDeleteProject = useCallback(async (id) => {
    const prev = projects;
    setProjects((p) => p.filter((proj) => proj._id !== id));
    try {
      await apiDeleteProject(id);
      if (projectId === id) navigate('/projects');
      // Chats detach to standalone — refresh that list so they reappear.
      const res = await getChatSessions({ force: true });
      setSessions(res.data.sessions || []);
    } catch {
      setProjects(prev);
    }
  }, [projects, projectId, navigate]);

  const handleSaveProjectFigures = useCallback(async ({ kpiInputs, capital }) => {
    setProjectSaving(true);
    try {
      const res = await apiUpdateProject(projectId, { kpiInputs, capital });
      setActiveProject(res.data.project);
      setProjects((prev) => prev.map((p) => (p._id === projectId ? res.data.project : p)));
    } finally {
      setProjectSaving(false);
    }
  }, [projectId]);

  // ─── Sending messages (standalone or project-scoped) ──────────────────────
  const handleSend = useCallback(async (text) => {
    setSending(true);
    setChatError('');
    if (!showProjectDetail) {
      setMessages((prev) => [...prev, { _id: `local-${Date.now()}`, role: 'user', content: text }]);
    }

    try {
      let activeId = sessionId;
      if (!activeId) {
        // No open thread — create one, inside the project if we're in one.
        if (inProjectArea && projectId) {
          const createRes = await apiCreateProjectSession(projectId);
          activeId = createRes.data.session._id;
          setProjectSessions((prev) => [createRes.data.session, ...prev]);
        } else {
          const createRes = await createChatSession();
          activeId = createRes.data.session._id;
          setSessions((prev) => [createRes.data.session, ...prev]);
        }
      }

      const res = await sendSessionMessage(activeId, text);
      setMessages((prev) => [
        ...prev.filter((m) => !String(m._id).startsWith('local-')),
        res.data.userMessage,
        res.data.assistantMessage,
      ]);
      if (inProjectArea && projectId) {
        setProjectSessions((prev) => {
          const others = prev.filter((s) => s._id !== res.data.session._id);
          return [res.data.session, ...others];
        });
      } else {
        setSessions((prev) => {
          const others = prev.filter((s) => s._id !== res.data.session._id);
          return [res.data.session, ...others];
        });
      }

      if (!sessionId) {
        navigate(
          inProjectArea && projectId
            ? `/projects/${projectId}/chat/${activeId}`
            : `/chat/${activeId}`,
          { replace: !inProjectArea }
        );
      }
    } catch (err) {
      setChatError(
        err.status === 503
          ? 'The assistant isn’t configured yet — add an Anthropic API key on the backend to enable it.'
          : (err.message || 'Could not reach the assistant. Try again.')
      );
    } finally {
      setSending(false);
    }
  }, [sessionId, projectId, inProjectArea, showProjectDetail, navigate]);

  const handleGenerateMilestones = useCallback(async () => {
    setGenerating(true);
    setMilestonesError('');
    try {
      const res = await apiGenerateMilestones();
      setMilestones((prev) => [...res.data.milestones, ...prev]);
      setRightOpen(true);
    } catch (err) {
      setMilestonesError(
        err.status === 503
          ? 'The assistant isn’t configured yet — add an Anthropic API key on the backend to enable it.'
          : (err.message || 'Could not generate milestones right now.')
      );
    } finally {
      setGenerating(false);
    }
  }, []);

  const handleUpdateMilestoneStatus = useCallback(async (id, status) => {
    const prevMilestones = milestones;
    setMilestones((prev) => prev.map((m) => (m._id === id ? { ...m, status } : m)));
    try {
      await apiUpdateMilestone(id, { status });
    } catch {
      setMilestones(prevMilestones);
    }
  }, [milestones]);

  // KPI onboarding gate only covers the standalone chat views — the
  // projects area has its own intake flow.
  const showGate = !inProjectArea && !kpiLoading && kpiProfile && kpiProfile.completionPct < 100 && !gateSkipped;

  const activeSession =
    sessions.find((s) => s._id === sessionId) ||
    projectSessions.find((s) => s._id === sessionId) ||
    null;

  return (
    <div className="chat-shell">
      {showGate && (
        <KpiGateModal
          profile={kpiProfile}
          onSaveSection={handleSaveKpiSection}
          onFinish={(updated) => setKpiProfile(updated)}
          onSkip={() => setGateSkipped(true)}
        />
      )}

      {intakeOpen && (
        <ProjectIntakeModal
          onClose={() => setIntakeOpen(false)}
          onCreated={handleProjectCreated}
        />
      )}

      <div className="chat-layout">
        <LeftSidebar
          sessions={sessions}
          activeSessionId={sessionId}
          onNewChat={handleNewChat}
          onSelectSession={handleSelectSession}
          onDeleteSession={handleDeleteSession}
          mobileOpen={leftOpen}
          onMobileClose={() => setLeftOpen(false)}
          user={user}
          renamingSessionId={renamingSessionId}
          renameDraft={renameDraft}
          onStartRename={handleStartRename}
          onRenameDraftChange={setRenameDraft}
          onSubmitRename={handleSubmitRename}
          onCancelRename={handleCancelRename}
          projects={projects}
          activeProjectId={projectId}
          onNewProject={handleNewProject}
          onSelectProject={handleSelectProject}
          onViewAllProjects={handleViewAllProjects}
        />

        {showProjectList ? (
          <ProjectsCenter
            projects={projects}
            loading={projectsLoading}
            onSelectProject={handleSelectProject}
            onNewProject={handleNewProject}
            onDeleteProject={handleDeleteProject}
            onOpenLeftDrawer={() => setLeftOpen(true)}
            onOpenRightDrawer={() => setRightOpen(true)}
          />
        ) : showProjectDetail ? (
          <ProjectDetailCenter
            project={activeProject}
            projectSessions={projectSessions}
            loading={projectLoading}
            sending={sending}
            chatError={chatError}
            onSend={handleSend}
            onSelectSession={(id) => navigate(`/projects/${projectId}/chat/${id}`)}
            onBack={() => navigate('/projects')}
            onOpenLeftDrawer={() => setLeftOpen(true)}
            onOpenRightDrawer={() => setRightOpen(true)}
          />
        ) : (
          <ChatCenter
            messages={messages}
            loading={messagesLoading}
            sending={sending}
            chatError={chatError}
            onSend={handleSend}
            onOpenLeftDrawer={() => setLeftOpen(true)}
            onOpenRightDrawer={() => setRightOpen(true)}
            session={activeSession}
            onRenameSession={handleRenameSession}
            breadcrumb={inProjectArea && activeProject ? activeProject.name : null}
            onBreadcrumb={() => navigate(`/projects/${projectId}`)}
          />
        )}

        <RightPanel
          profile={kpiProfile}
          milestones={milestones}
          onGenerate={handleGenerateMilestones}
          generating={generating}
          milestonesError={milestonesError}
          onUpdateStatus={handleUpdateMilestoneStatus}
          mobileOpen={rightOpen}
          onMobileClose={() => setRightOpen(false)}
          project={inProjectArea ? activeProject : null}
          onSaveProjectFigures={handleSaveProjectFigures}
          projectSaving={projectSaving}
        />
      </div>
    </div>
  );
};

export default Profile;
